import { format, formatDistanceToNow, differenceInDays } from 'date-fns';

// Module-level currency — updated by CurrencyContext on mount
let _activeCurrency = 'USD';
export const setActiveCurrency = (c) => { _activeCurrency = c; };

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹',
  AED: 'AED ', SAR: 'SAR ', SGD: 'S$', AUD: 'A$',
  CAD: 'C$', JPY: '¥', CNY: '¥',
};

/**
 * Format currency with symbol and locale.
 * Uses the active currency set by CurrencyContext (or explicit override).
 */
export const formatCurrency = (amount, currency) => {
  if (amount === null || amount === undefined) return '-';
  const num = Number(amount);
  if (isNaN(num)) return '-';
  const cur = currency || _activeCurrency;
  const symbol = CURRENCY_SYMBOLS[cur] || cur + ' ';
  if (Math.abs(num) >= 10000000) return `${symbol}${(num / 10000000).toFixed(2)} Cr`;
  if (Math.abs(num) >= 100000) return `${symbol}${(num / 100000).toFixed(2)} L`;
  return `${symbol}${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Format date to readable string
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  } catch {
    return '-';
  }
};

/**
 * Format date with time
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy, HH:mm');
  } catch {
    return '-';
  }
};

/**
 * Relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return '-';
  }
};

/**
 * Get days until a future date
 */
export const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  try {
    return differenceInDays(new Date(dateStr), new Date());
  } catch {
    return null;
  }
};

/**
 * Check if a date has passed
 */
export const isExpired = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};

/**
 * Check if expiring within N days (default 30)
 */
export const isExpiringSoon = (dateStr, daysThreshold = 30) => {
  if (!dateStr) return false;
  const days = getDaysUntil(dateStr);
  return days !== null && days >= 0 && days <= daysThreshold;
};

/**
 * Get status color for MUI components
 */
export const getStatusColor = (status) => {
  if (!status) return 'default';
  const map = {
    draft: 'default',
    pending: 'warning',
    pending_approval: 'warning',
    submitted: 'info',
    approved: 'success',
    active: 'success',
    completed: 'success',
    cancelled: 'error',
    rejected: 'error',
    open: 'info',
    closed: 'default',
    awarded: 'success',
    sent: 'info',
    shipped: 'info',
    delivered: 'success',
    compliant: 'success',
    non_compliant: 'error',
    expiring_soon: 'warning',
    expired: 'error',
  };
  return map[status.toLowerCase().replace(/[\s-]/g, '_')] || 'default';
};

/**
 * Truncate text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

/**
 * Generate a random access code
 */
export const generateAccessCode = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < length; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Get initials from a name
 */
export const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

/**
 * Calculate percentage
 */
export const calcPercentage = (value, total) => {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
};
